import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { SvgIcon } from '@ui-elements';
import { CompareRangeVariantTabItem } from '../model-landing-campare-variant-tab-item';
import { setCompareRangeSelectedVariants } from '~/common/services/compare-range-modal-service';
import { PimModelVariant } from '../../store';
import styles from './index.module.scss';

type CarouselOption = {
  percentage: number;
  centerMode: boolean;
  swipeable: boolean;
  hasPrev?: boolean;
  hasNext?: boolean;
};

export interface CompareRangeTabItem {
  id?: string;
  name?: string;
  imageSrc?: string;
  selected?: boolean;
}

interface CompareRangeTabProp {
  className?: string;
  modelName?: string;
  section?: 'range' | 'variant';
  tabItems: CompareRangeTabItem[];
  onChange: (variant: CompareRangeTabItem) => void;
  pimModelVariants?: PimModelVariant[];
  hideCompareRangeButton?: boolean;
}

export const CompareRangeVariantTab = React.memo<CompareRangeTabProp>(
  ({
    tabItems,
    className,
    modelName,
    section,
    onChange,
    pimModelVariants,
    hideCompareRangeButton,
  }) => {
    const [activeTab, setActiveTab] = useState<CompareRangeTabItem | null>(
      null
    );

    const carouselRef = React.useRef<Carousel>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const [options, setOptions] = React.useState<CarouselOption>({
      centerMode: true,
      swipeable: true,
      percentage: 100 / 3,
      hasPrev: true,
      hasNext: true,
    });

    useEffect(() => {
      if (tabItems?.length > 0) {
        const item = tabItems.find((v) => v.selected) ?? tabItems[0];
        setActiveTab(item as CompareRangeTabItem);
        onChange(item as CompareRangeTabItem);
        return;
      }
    }, []);

    useEffect(() => onChange(activeTab as CompareRangeTabItem), [activeTab]);

    useLayoutEffect(() => {
      if (isMobile()) {
        setOptions({
          centerMode: true,
          swipeable: true,
          percentage: 100 / 3,
          hasPrev: true,
          hasNext: true,
        });
      }
    }, []);

    const isMobile = () => {
      if (containerRef.current === null) return false;
      return containerRef.current?.clientWidth < 923;
    };

    const mapPimModelVariants = () => {
      const modelVariants = pimModelVariants?.map((item) => ({
        id: '',
        modelPimIdentifier: item.modelPimIdentifier,
        variantPimIdentifier: item.variantPimIdentifier,
        _order: Number.MAX_SAFE_INTEGER,
      }));
      return modelVariants || [];
    };

    const storeVariants = () => {
      const modelVariants = mapPimModelVariants();
      setCompareRangeSelectedVariants(modelVariants);
    };

    return (
      <div ref={containerRef} className={className}>
        <div className={styles.variantTabWrapper}>
          <Carousel
            ref={carouselRef}
            interval={1000 * 60}
            autoPlay={false}
            showThumbs={false}
            showStatus={false}
            infiniteLoop={false}
            showIndicators={false}
            swipeable={options.swipeable}
            centerMode={options.centerMode}
            centerSlidePercentage={options.percentage}
            className={styles.variantTabMobile}
          >
            {tabItems.map((item, index) => (
              <div key={index} className={styles.variantTabItem}>
                <CompareRangeVariantTabItem
                  isSelected={item.id === activeTab?.id}
                  variantImageUrl={item.imageSrc as string}
                  variantName={item.name as string}
                  onTabClick={() => {
                    window['pushDigitalData'](
                      {
                        event: '_formInteract',
                        form: {
                          name: 'model page',
                          stage: section === 'range' ? 'compare' : 'showroom',
                          field: {
                            name: 'variant',
                            value: item.name as string,
                          },
                        },
                      },
                      true
                    );
                    setActiveTab(item);
                  }}
                />
              </div>
            ))}
          </Carousel>

          {modelName && isMobile() && !hideCompareRangeButton && (
            <AddCompareModelCTA
              isMobile={isMobile()}
              onClick={storeVariants}
              modelName={modelName}
            />
          )}

          <ul className={styles.variantTabDesktop}>
            {tabItems.map((item, index) => (
              <li key={index} className={styles.variantTabItem}>
                <CompareRangeVariantTabItem
                  isSelected={item.id === activeTab?.id}
                  variantImageUrl={item.imageSrc as string}
                  variantName={item.name as string}
                  onTabClick={() => {
                    window['pushDigitalData'](
                      {
                        event: '_formInteract',
                        form: {
                          name: 'model page',
                          stage: section === 'range' ? 'compare' : 'showroom',
                          field: {
                            name: 'variant',
                            value: item.name as string,
                          },
                        },
                      },
                      true
                    );
                    setActiveTab(item);
                  }}
                />
              </li>
            ))}
            {modelName && !hideCompareRangeButton && (
              <li className={styles.compareCTA_desktop}>
                <AddCompareModelCTA
                  isMobile={isMobile()}
                  onClick={storeVariants}
                  modelName={modelName}
                />
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  }
);

interface AddCompareModelCTAProp {
  isMobile: boolean;
  onClick: () => void;
  modelName: string;
}

export const AddCompareModelCTA = React.memo<AddCompareModelCTAProp>(
  ({ isMobile, onClick, modelName }) => {
    const [pathName, setPathName] = useState<string | null>();
    useEffect(() => setPathName(location.pathname), []);

    return (
      <div
        className={
          isMobile ? styles.compareCTA_mobile : styles.compareCTA_desktopInner
        }
      >
        <a href={`/compare-cars?returnUrl=${pathName}`} onClick={onClick}>
          <SvgIcon type="compareCars" color="#1637a0" />
          <span>Compare the {modelName} range</span>
        </a>
      </div>
    );
  }
);
